import React, { useState, useEffect } from 'react';
import FinanceLayout from '../../../../../../layout/components/FinanceLayout';
import MonthlyDateSelect from '../../../../../../components/AppInput/MonthlyDateSelect';
import { CircularProgress, Dialog, DialogContent } from '@mui/material';
import gstReportServices from '../gstReportServices';

function GSTR1Page() {

    const [loading, setLoadingStatus] = useState(false);
    const [auditInfo, setAuditInfo] = useState();
    const [summary, setSummary] = useState();
    const [netTaxLiability, setNetTaxLiability] = useState();
    const [filter, setFilter] = useState(null);

    const [dialogStatus, setDialogStatus] = useState(false);
    const [jsonData, setJsonData] = useState(null);

    const getVoucherList = () => {
        setLoadingStatus(true);
        gstReportServices.getGSTR1Summary(filter).then(res => {
            setSummary(res.data.summary);
            setAuditInfo(res.data.auditInfo);
            setNetTaxLiability(res.data.netTaxLiability);
        }).catch(error => {
            console.error("GSTR1 Summary loading failed:", error);
        }).finally(() => {
            setLoadingStatus(false);
        });
    };
    
    useEffect(() => {
        if (filter) {
            getVoucherList();
        }
    }, [filter]);

    const getJSON = () => {
        setDialogStatus(true);
        gstReportServices.getGSTR1Json(filter).then(res => {
            setJsonData(res.payload);
        }).catch(error => {
            console.error("GSTR1 JSON export failed:", error);
        });
    };

    return (
        <FinanceLayout>
            <div className='row my-2'>
                <div className='col-md-6'>
                    <div className="fw-bold h5 py-2">GSTR1 Summary (Outward Supplies / Tax Liability)</div>
                </div>

                <div className='col-md-6'>
                    <div className="bg-white p-2 d-flex justify-content-end align-items-center gap-2">
                        <MonthlyDateSelect filter={filter} setFilter={setFilter} />
                        <button className='btn btn-outline-success' onClick={getJSON}>Get JSON</button>
                    </div>
                </div>

                <Dialog
                    open={dialogStatus}
                    keepMounted
                    onClose={() => setDialogStatus(false)}
                    fullWidth={true}
                    maxWidth="lg"
                >
                    <DialogContent>
                        <div className="mb-2 fw-bold text-secondary">Raw GSTR-1 Government Payload Preview:</div>
                        <pre className="bg-light p-3 border rounded text-dark" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                            {JSON.stringify(jsonData, null, 2)}
                        </pre>
                    </DialogContent>
                </Dialog>
            </div>

            {loading && 
              <div className='text-center mt-5'>
                <CircularProgress />
              </div>
            }

            {!loading && auditInfo && summary && netTaxLiability &&
                <div className='row my-2'>
                    {/* --- AUDIT INFORMATION TRIANGULATION GRID --- */}
                    <div className='col-md-12'>
                        <table className='table table-bordered table-sm mt-3'>
                            <thead>
                                <tr className='table-dark'>
                                    <th>Total Outward Vouchers</th>
                                    <th>{auditInfo.totalVouchers}</th>
                                </tr>
                                <tr className='table-primary'>
                                    <td>Included In Return Liability Engine</td>
                                    <td>{auditInfo.includedInReturn}</td>
                                </tr>
                                <tr className='table-warning'>
                                    <td>Excluded from Return Engine</td>
                                    <td></td>
                                </tr>
                                <tr className='table-info'>
                                    <td><span className='ms-5'>Draft Vouchers Excluded</span></td>
                                    <td>{auditInfo.draftVouchersExcluded}</td>
                                </tr>
                                <tr className='table-info'>
                                    <td><span className='ms-5'>Canceled Vouchers Excluded</span></td>
                                    <td>{auditInfo.canceledVouchersExcluded}</td>
                                </tr>
                            </thead>
                        </table>
                    </div>

                    {/* --- STATUTORY GSTR-1 PARTICULARS TABLE --- */}
                    <div className='col-md-12 mt-2'>
                        <table className='table table-bordered table-sm mt-3'>
                            <thead>
                                <tr className='table-dark text-center'>
                                    <td>#</td>
                                    <td>Particulars (Outward Streams)</td>
                                    <td>Voucher Count</td>
                                    <td>Taxable Amount</td>
                                    <td>CGST</td>
                                    <td>SGST</td>
                                    <td>IGST</td>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <th className="text-center">1</th>
                                    <th>B2B Invoices - 4A, 4B, 4C, 6B, 6C</th>
                                    <td className="text-center">{summary.b2b.count}</td>
                                    <td className="text-end">{summary.b2b.taxable.toFixed(2)}</td>
                                    <td className="text-end">{summary.b2b.cgst.toFixed(2)}</td>
                                    <td className="text-end">{summary.b2b.sgst.toFixed(2)}</td>
                                    <td className="text-end">{summary.b2b.igst.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <th className="text-center">2</th>
                                    <th>B2C (Large) Invoice - 5A, 5B</th>
                                    <td className="text-center">{summary.b2cLarge.count}</td>
                                    <td className="text-end">{summary.b2cLarge.taxable.toFixed(2)}</td>
                                    <td className="text-end">{summary.b2cLarge.cgst.toFixed(2)}</td>
                                    <td className="text-end">{summary.b2cLarge.sgst.toFixed(2)}</td>
                                    <td className="text-end">{summary.b2cLarge.igst.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <th className="text-center">3</th>
                                    <th>B2C (Small) Invoice - 7</th>
                                    <td className="text-center">{summary.b2cSmall.count}</td>
                                    <td className="text-end">{summary.b2cSmall.taxable.toFixed(2)}</td>
                                    <td className="text-end">{summary.b2cSmall.cgst.toFixed(2)}</td>
                                    <td className="text-end">{summary.b2cSmall.sgst.toFixed(2)}</td>
                                    <td className="text-end">{summary.b2cSmall.igst.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <th className="text-center">4</th>
                                    <th>Credit/Debit Note (Registered) - 9B</th>
                                    <td className="text-center">{summary.cdnr.count}</td>
                                    <td className="text-end text-danger">-{summary.cdnr.taxable.toFixed(2)}</td>
                                    <td className="text-end text-danger">-{summary.cdnr.cgst.toFixed(2)}</td>
                                    <td className="text-end text-danger">-{summary.cdnr.sgst.toFixed(2)}</td>
                                    <td className="text-end text-danger">-{summary.cdnr.igst.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <th className="text-center">5</th>
                                    <th>Credit/Debit Note (Unregistered) - 9B</th>
                                    <td className="text-center">{summary.cdnur.count}</td>
                                    <td className="text-end text-danger">-{summary.cdnur.taxable.toFixed(2)}</td>
                                    <td className="text-end text-danger">-{summary.cdnur.cgst.toFixed(2)}</td>
                                    <td className="text-end text-danger">-{summary.cdnur.sgst.toFixed(2)}</td>
                                    <td className="text-end text-danger">-{summary.cdnur.igst.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <th className="text-center">6</th>
                                    <th>Nil Rated Invoices - 8A, 8B, 8C, 8D</th>
                                    <td className="text-center">{summary.nilRated.count}</td>
                                    <td className="text-end">{summary.nilRated.taxable.toFixed(2)}</td>
                                    <td className="text-end">{summary.nilRated.cgst.toFixed(2)}</td>
                                    <td className="text-end">{summary.nilRated.sgst.toFixed(2)}</td>
                                    <td className="text-end">{summary.nilRated.igst.toFixed(2)}</td>
                                </tr>
                            </tbody>
                            <tfoot>
                                <tr className='table-info fw-bold'>
                                    <th colSpan={3}><span className='float-end me-5'>Net Outward Tax Liability Total</span></th>
                                    <th className="text-end">{netTaxLiability.taxable.toFixed(2)}</th>
                                    <th className="text-end text-dark">{netTaxLiability.cgst.toFixed(2)}</th>
                                    <th className="text-end text-dark">{netTaxLiability.sgst.toFixed(2)}</th>
                                    <th className="text-end text-dark">{netTaxLiability.igst.toFixed(2)}</th>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            }
        </FinanceLayout>
    );
}

export default GSTR1Page;